import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { GlobalToastTypeNames, ToastTypes } from 'types';
import {
  useMarkInstallmentPaidManuallyMutation,
  useRevertInstallmentManualPaymentMutation,
  StudentDetailDocument,
} from 'core/graphql';

import { useToasts } from '../Toast/useToasts';

interface IMarkPaidManuallyArgs {
  contributionId: string;
  /** YYYY-MM-DD; defaults to today at the call site. */
  date: string;
  /** Omit to let the backend cover the full remaining debt. */
  amount?: number;
}

interface IContributionMutationsHook {
  /** ТЗ 0004: mark a взнос paid by hand (cash / undocumented transfer). */
  markPaidManually: (args: IMarkPaidManuallyArgs) => Promise<boolean>;
  /** ТЗ 0004: undo the manual mark — removes the hidden manual Payment. */
  revertManualPayment: (contributionId: string) => Promise<boolean>;
  loading: boolean;
}

/**
 * Unified-contribution mutations (spec 0004). The backend provisions /
 * removes the hidden `Payment(source='manual_paid')` under the hood — the
 * UI only talks in terms of the взнос. Refetches `StudentDetail` (schedule
 * chips, KPI strip) and the invoices list.
 *
 * `customErrors.code` values (CONTRIBUTION_HAS_INVOICE, MANUAL_ALREADY_SET,
 * NOT_MANUAL, PAYMENT_HAS_INVOICE, PAYMENT_HAS_CORRECTIONS,
 * CURRENCY_MISMATCH) are translated to human text; unknown codes fall back
 * to the backend message.
 */
export const useContributionMutations = (
  studentId?: string,
): IContributionMutationsHook => {
  const { t } = useTranslation();
  const { addToast } = useToasts();

  const refetchQueries = useMemo(() => {
    const queries: Array<{
      query: typeof StudentDetailDocument;
      variables?: { id: string };
    }> = [];
    // NOTE (spec 0008 round 2): the no-variables FakturowniaInvoicesDocument
    // refetch (an UNBOUNDED company-wide query on every mutation) was
    // removed — the card's invoice blocks read the drain hook, which has its
    // own refetch() callbacks.
    if (studentId) {
      queries.push({
        query: StudentDetailDocument,
        variables: { id: studentId },
      });
    }
    return queries;
  }, [studentId]);

  const [markMutation, markState] = useMarkInstallmentPaidManuallyMutation({
    refetchQueries,
  });
  const [
    revertMutation,
    revertState,
  ] = useRevertInstallmentManualPaymentMutation({
    refetchQueries,
  });

  const errText = useCallback(
    (
      errors?: Array<{
        message?: string | null;
        code?: string | null;
      } | null> | null,
    ) =>
      errors
        ?.map((e) => {
          if (e?.code) {
            const translated = t(
              `pages.studentDetail.contributions.errors.${e.code}`,
              { defaultValue: '' },
            );
            if (translated) return translated;
          }
          return e?.message || e?.code || null;
        })
        .filter(Boolean)
        .join('\n') || undefined,
    [t],
  );

  const notifySuccess = useCallback(
    (title: string) => {
      addToast({
        data: { type: ToastTypes.success, title },
        type: GlobalToastTypeNames.FakturowniaMatch,
      });
    },
    [addToast],
  );

  const notifyError = useCallback(
    (title: string, text?: string) => {
      addToast({
        data: { type: ToastTypes.error, title, text },
        type: GlobalToastTypeNames.FakturowniaMatch,
      });
    },
    [addToast],
  );

  const markPaidManually = useCallback(
    async ({ contributionId, date, amount }: IMarkPaidManuallyArgs) => {
      try {
        const result = await markMutation({
          variables: { contributionId, date, amount },
        });
        const payload = result.data?.markContributionPaidManually;
        if (payload?.success) {
          notifySuccess(t('pages.studentDetail.payments.toasts.markedManual'));
          return true;
        }
        notifyError(
          t('pages.studentDetail.payments.toasts.markManualFailed'),
          errText(payload?.customErrors),
        );
        return false;
      } catch (error) {
        notifyError(
          t('pages.studentDetail.payments.toasts.markManualFailed'),
          (error as Error).message,
        );
        return false;
      }
    },
    [markMutation, notifySuccess, notifyError, errText, t],
  );

  const revertManualPayment = useCallback(
    async (contributionId: string) => {
      try {
        const result = await revertMutation({ variables: { contributionId } });
        const payload = result.data?.revertManualContributionPayment;
        if (payload?.success) {
          notifySuccess(
            t('pages.studentDetail.payments.toasts.manualReverted'),
          );
          return true;
        }
        notifyError(
          t('pages.studentDetail.payments.toasts.revertManualFailed'),
          errText(payload?.customErrors),
        );
        return false;
      } catch (error) {
        notifyError(
          t('pages.studentDetail.payments.toasts.revertManualFailed'),
          (error as Error).message,
        );
        return false;
      }
    },
    [revertMutation, notifySuccess, notifyError, errText, t],
  );

  return {
    markPaidManually,
    revertManualPayment,
    loading: markState.loading || revertState.loading,
  };
};
